import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { RotateCcw, Eye, Plus } from 'lucide-react';

const PAGE_SIZE = 25;

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CreditNotes() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.creditNoteList(token, params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error');
    } finally { setLoading(false); }
  }, [token, fromDate, toDate, page, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? items.filter(cn => `${cn.note_no||''} ${cn.customer_name||''} ${cn.reason||''}`.toLowerCase().includes(search.toLowerCase()))
    : items;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="credit-notes-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center">
            <RotateCcw size={20} className="text-orange-600" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">Credit Notes</h1>
            <p className="text-gray-500 text-xs">{total} note{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Link to="/notes/credit/new" data-testid="new-credit-note-btn"
          className="flex items-center gap-1.5 px-5 h-11 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold hover:opacity-90 transition-opacity">
          <Plus size={16} /> New Credit Note
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="text" placeholder="Search CN#, customer, reason..." value={search} onChange={e => setSearch(e.target.value)}
          data-testid="cn-search" className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm w-56 focus:border-brand-purple outline-none" />
        <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} data-testid="cn-from-date"
          className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
        <span className="text-gray-400 text-xs">to</span>
        <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} data-testid="cn-to-date"
          className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
      </div>

      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="credit-notes-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Date</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">CN Number</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Sale Ref</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Customer</th>
                <th className="text-right px-5 py-3 text-gray-500 text-xs font-bold uppercase">Amount Returned</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Reason</th>
                <th className="text-right px-5 py-3 text-gray-500 text-xs font-bold uppercase w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {Array.from({ length: 7 }).map((_, j) => <td key={j} className="px-5 py-3"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <RotateCcw size={36} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm mb-2">No credit notes yet</p>
                    <Link to="/notes/credit/new" className="text-brand-purple text-sm font-bold hover:underline">Create your first credit note</Link>
                  </td>
                </tr>
              ) : filtered.map(cn => (
                <tr key={cn.id} data-testid={`cn-row-${cn.id}`}
                  className="border-b border-gray-100 hover:bg-gray-50/60 cursor-pointer transition-colors"
                  onClick={() => navigate(`/notes/credit/${cn.id}`)}>
                  <td className="px-5 py-3 text-gray-900 font-semibold whitespace-nowrap">{fmtDate(cn.date)}</td>
                  <td className="px-5 py-3 text-gray-900 font-bold">{cn.note_no}</td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{cn.sale_id?.slice(0, 8)}...</td>
                  <td className="px-5 py-3 text-gray-700">{cn.customer_name || '—'}</td>
                  <td className="px-5 py-3 text-right text-red-600 font-bold tabular-nums">{formatMoney(cn.total_returned)}</td>
                  <td className="px-5 py-3 text-gray-500 text-xs max-w-[200px] truncate">{cn.reason}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={e => { e.stopPropagation(); navigate(`/notes/credit/${cn.id}`); }}
                      data-testid={`cn-view-${cn.id}`} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <span className="text-gray-500 text-sm">{total} note{total !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="px-3 py-1.5 text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
